/**
 * WBTagalysSearch - client for Tagalys' v2 storefront search (/v2/search,
 * Tagalys' Postman collection of 30.09.2026; v2 only, as Tagalys asked
 * 07.10.2026). Only active when useTagalysSearch is true. Transport: assets/tagalys-api.js; analytics go
 * through Tagalys' SDK (snippets/tagalys-analytics.liquid).
 */
(function () {
  var LABEL = '[WBTagalysSearch]';

  // Search results. v2: GET /v2/search with include[]=products (Shopify-format
  // products, WBTagalysSearchAdapter.fromProducts builds the cards) and
  // total_count; returns {total, products, query, page, per_page, redirect_url},
  // or null on failure (logged).
  // options: {page, per_page, sort, signal}.
  function search(q, options) {
    options = options || {};
    var params = {
      query: q,
      include: ['products', 'total_count'],
      page: options.page || 1,
      per_page: options.per_page || 40
    };
    if (options.sort) params.sort = options.sort;
    return window.WBTagalysApi.get('/v2/search', params, { label: LABEL, signal: options.signal }).then(function (response) {
      if (!response || response.error || !Array.isArray(response.products)) {
        if (response && response.error) console.error(LABEL, '/v2/search', response.error.code || response.error);
        return null;
      }
      return {
        total: response.total_count || 0,
        products: response.products,
        query: response.query,
        page: response.page,
        per_page: response.per_page,
        redirect_url: response.redirect_url || null
      };
    }).then(function (result) {
      // Tagalys answered with no product for this query (and no redirect): show
      // Shopify's own results instead of an empty overlay (first page only, see
      // shopifySearch). A failed Tagalys request (null) stays null; a failed
      // fallback leaves the empty result.
      if (result && !result.products.length && !result.redirect_url && (options.page || 1) === 1) {
        return shopifySearch(q, options).then(function (fallback) { return fallback || result; });
      }
      return result;
    });
  }

  // Shopify's own search, through the JSON view templates/search.athlete-search.liquid
  // (the athlete shop's search source, see snippets/athlete-search.liquid). Used
  // by search() when Tagalys answers with no product. Same result shape as
  // search(), plus source: 'shopify' so the overlay does not report these
  // products as a Tagalys search impression.
  //
  // Shopify matches titles, descriptions, tags, vendor, type and SKUs with typo
  // tolerance, its "fields" option is ignored on our stores (search view and
  // predictive search, tested 14.10.2026: "iod" gave 54 unrelated products),
  // and it ranks fuzzy hits above exact ones (Sea Iodine was 50th of 54). So:
  // read up to MAX_PAGES pages (page 1 first, the rest in parallel, skipped
  // when page 1 already fills a result page), keep only products whose title
  // or tags contain every typed word at a word start, one result page.
  var FALLBACK_MAX_PAGES = 3;

  function shopifySearch(q, options) {
    options = options || {};
    var signal = options.signal;
    var words = queryWords(q);
    if (!words.length || (signal && signal.aborted)) return Promise.resolve(null);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var base = root + 'search?view=athlete-search&type=product&options%5Bprefix%5D=last&q=' + encodeURIComponent(q) + '&page=';

    function getPage(page) {
      var url = base + page;
      return fetch(url, { headers: { Accept: 'application/json' }, signal: signal }).then(function (response) {
        if (!response.ok) {
          console.error(LABEL, 'HTTP error', url, response.status, response.statusText);
          return null;
        }
        return response.json();
      }).then(function (data) {
        return data && Array.isArray(data.products) ? data : null;
      }).catch(function (error) {
        if (error && error.name !== 'AbortError') console.error(LABEL, 'fetch error', url, error);
        return null;
      });
    }

    function matching(data) {
      return data.products.filter(function (product) { return productMatches(product, words); });
    }

    return getPage(1).then(function (first) {
      if (!first) return null;
      var perPage = first.per_page || 40;
      var kept = matching(first);
      var pages = Math.min(Math.ceil((first.total || 0) / perPage), FALLBACK_MAX_PAGES);
      if (kept.length >= perPage || pages < 2 || (signal && signal.aborted)) {
        return fallbackResult(q, kept.slice(0, perPage), perPage);
      }
      var more = [];
      for (var page = 2; page <= pages; page++) more.push(getPage(page));
      return Promise.all(more).then(function (rest) {
        rest.forEach(function (data) { if (data) kept = kept.concat(matching(data)); });
        return fallbackResult(q, kept.slice(0, perPage), perPage);
      });
    });
  }

  function fallbackResult(q, products, perPage) {
    return {
      total: products.length,
      products: products,
      query: q,
      page: 1,
      per_page: perPage,
      redirect_url: null,
      source: 'shopify'
    };
  }

  // Lowercase, accents removed ("ü" -> "u", "ß" -> "ss"), split into alphanumeric tokens.
  function tokens(text) {
    text = String(text || '').toLowerCase().replace(/ß/g, 'ss');
    if (text.normalize) text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return text.split(/[^a-z0-9]+/).filter(Boolean);
  }

  // The typed words worth matching: 2+ characters, at most 8 (longer input is noise).
  function queryWords(q) {
    return tokens(q).filter(function (word) { return word.length > 1; }).slice(0, 8);
  }

  // Every typed word starts a token of the title or of a tag.
  function productMatches(product, words) {
    var haystack = tokens(product.title).concat(tokens((product.tags || []).join(' ')));
    return words.every(function (word) {
      for (var i = 0; i < haystack.length; i++) {
        if (haystack[i].indexOf(word) === 0) return true;
      }
      return false;
    });
  }

  // Query suggestions for the search overlay ("Search suggestions (N)").
  // Tagalys' suggestions return no queries on our plan, so this reads Shopify's
  // own predictive search (/search/suggest.json, type=query) instead - one GET
  // on the storefront domain, no external search. Resolves to up to `limit` lowercase
  // query strings ([] on error/abort). When the typed text is itself a whole
  // word/phrase of a suggestion (e.g. "power" -> "power high waist leggings")
  // it is listed first, as the legacy search's suggestions did; a fragment like "leg"
  // is not. options: {limit, signal}.
  function querySuggestions(q, options) {
    options = options || {};
    var limit = options.limit || 5;
    var term = String(q || '').trim().toLowerCase();
    if (!term) return Promise.resolve([]);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'search/suggest.json?q=' + encodeURIComponent(term) +
      '&resources[type]=query&resources[limit]=' + limit;
    return fetch(url, { headers: { Accept: 'application/json' }, signal: options.signal }).then(function (response) {
      if (!response.ok) {
        console.error(LABEL, 'HTTP error', url, response.status, response.statusText);
        return null;
      }
      return response.json();
    }).catch(function (error) {
      if (error && error.name !== 'AbortError') console.error(LABEL, 'fetch error', url, error);
      return null;
    }).then(function (body) {
      var items = (body && body.resources && body.resources.results && body.resources.results.queries) || [];
      var queries = [];
      items.forEach(function (item) {
        var text = String((item && item.text) || '').trim().toLowerCase();
        if (text && queries.indexOf(text) === -1) queries.push(text);
      });
      var escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      var wholeWord = new RegExp('(^|\\s)' + escaped + '(\\s|$)');
      if (queries.length && queries.indexOf(term) === -1 && queries.some(function (text) { return wholeWord.test(text); })) {
        queries.unshift(term);
      }
      return queries.slice(0, limit);
    });
  }

  // Search results impression (search_results_viewed) through Tagalys' SDK -
  // see snippets/tagalys-analytics.liquid. Product views, add-to-carts and
  // orders come from Tagalys' app pixel, so they are not sent from here.
  // details: {q, page, total, productIds} with productIds = the product IDs shown.
  function trackSearchImpression(details) {
    if (!details || !details.productIds || !details.productIds.length || !window.wbTagalysAnalytics) return;
    // Shopify fallback results (shopifySearch) are not Tagalys results.
    if (details.source === 'shopify') return;
    window.wbTagalysAnalytics(function (analytics) {
      analytics.trackEvent('search_results_viewed', {
        query: details.q,
        page: details.page,
        total_count: details.total,
        product_ids: details.productIds.map(String)
      });
    });
  }

  window.WBTagalysSearch = {
    search: search,
    querySuggestions: querySuggestions,
    trackSearchImpression: trackSearchImpression
  };
})();
